import React, { useRef } from 'react';
import useFieldApi, { UseFieldApiConfig } from '@data-driven-forms/react-form-renderer/dist/cjs/use-field-api';
import { FieldWrapper } from './FieldWrapper';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import MaskedInput from 'react-text-mask';
import { phoneNumberMask, removePhoneSpecialChars, dateMask, isRequiredValidation } from '../../utils';
import ReactTooltip from 'react-tooltip';
import { useOverflow } from '../../hooks';

interface Props {
  name: string;
  wrapperClasses?: string;
  label?: string;
  placeholder?: string;
  toolTip?: string;
  fieldClasses?: string;
  isError?: boolean;
  error?: string;
  Right?: React.ReactNode;
  [key: string]: any;
}

export const MaskedDateInput = React.forwardRef<HTMLInputElement, Props>(
  ({ name, placeholder, isError, ...input }, ref) => {
    const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const newEvent = { ...e, target: { ...e.target, value: e.target.value } };
      input.onChange(newEvent);
    };

    return (
      <div
        className={clsx('form-field react-datepicker_custom', isError && 'shadow-field-error')}
        data-testid='maskedDate-input-wrapper'
      >
        <MaskedInput
          id={name}
          name={name}
          type='text'
          mask={dateMask}
          placeholder={placeholder}
          keepCharPositions
          guide
          autoComplete='off'
          {...input}
          // @ts-ignore
          ref={ref}
          aria-invalid={isError}
          aria-required={input.required}
          onChange={onChange}
          required={input.required}
          className='placeholder-gray-default mb-0 border-0 focus:border-0 focus:outline-none'
          style={{ width: '6.5rem' }}
        />
      </div>
    );
  }
);

export const MaskedTextInput = React.forwardRef<HTMLInputElement, Props>(
  ({ name, isPhone, label, placeholder, toolTip, fieldClasses, isError, Right, wrapperClassName, ...input }, ref) => {
    const divRef = useRef<HTMLDivElement>(null);
    const { overflowActive } = useOverflow(divRef?.current?.firstChild);

    const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const formatted = removePhoneSpecialChars(e.target.value);
      const newEvent = { ...e, target: { ...e.target, value: formatted } };
      input.onChange(newEvent);
    };

    return (
      <div
        ref={divRef}
        data-tip={input.value}
        data-for={name}
        className={clsx('field-with-icons', isError && 'shadow-field-error', wrapperClassName)}
        data-testid='maskedText-input-wrapper'
      >
        <MaskedInput
          id={name}
          name={name}
          type='tel'
          mask={phoneNumberMask}
          placeholder={placeholder}
          keepCharPositions
          guide
          autoComplete='off'
          {...input}
          ref={ref as any}
          aria-describedby={name && `error-for-${name}`}
          aria-invalid={isError}
          aria-required={input.required}
          onChange={onChange}
          required={input.required}
          className={clsx(
            'truncate form-field placeholder-gray-default mb-0 border-0 focus:border-0',
            fieldClasses,
            Right && 'rounded-r-none'
          )}
        />
        {Right && (
          <div className='flex items-center bg-white' data-testid='right-component'>
            {Right}
          </div>
        )}
        {overflowActive && (
          <ReactTooltip
            id={name}
            className='tooltip-custom'
            place='right'
            effect='solid'
            type='light'
            multiline
            border
            borderColor='#5b2f91'
          />
        )}
      </div>
    );
  }
);

export const MaskedTextField = React.forwardRef<HTMLInputElement, Props>(
  ({ name, wrapperClasses, label, toolTip, isError, error, validate, component, ...props }, ref) => {
    const { t } = useTranslation('form');
    const isRequired = isRequiredValidation(validate);
    return (
      <FieldWrapper
        className={wrapperClasses}
        toolTip={toolTip}
        name={name}
        label={label ? t(label) : label}
        isError={isError}
        error={error}
      >
        <MaskedTextInput {...props} name={name} ref={ref} isError={isError} required={isRequired} />
      </FieldWrapper>
    );
  }
);

/* istanbul ignore next */ /* this code is not used or will be deleted in other branches */
export function MaskedTextFieldDdf({ colSpan, colWidth, colStart, ...props }: UseFieldApiConfig) {
  const { input, meta } = useFieldApi(props);
  const { modified, error } = meta;
  const isError = modified && error && typeof error === 'string';

  return <MaskedTextField {...props} {...input} isError={isError} error={error} />;
}
